import { RadioButton } from "@/lib/Components/Form/Inputs/RadioButton";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import {
  CreateRelocationInput,
  Currency,
  RelocationInclusionInput,
  RelocationInclusionType,
} from "@/gql/graphql";
import { useState } from "react";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { useTranslations } from "use-intl";

type FuelOffer = "no_fuel" | "free_tank" | "refund";

export function RelocationFuelInclusionInput({
  currency,
}: {
  currency?: Currency;
}) {
  const { values, setFieldValue } = useFormState<CreateRelocationInput>();
  const t = useTranslations("relocation.inclusions.fuel");

  const fuelInclusionIdx = values.inclusions?.upsert?.findIndex(
    (inclusion) => inclusion.type === RelocationInclusionType.Fuel,
  );
  const fuelInclusion = values.inclusions?.upsert?.find(
    (inclusion) => inclusion.type === RelocationInclusionType.Fuel,
  );

  let initialFuelOffer: FuelOffer = "no_fuel";
  if (fuelInclusion) {
    if (fuelInclusion.has_free_tank) {
      initialFuelOffer = "free_tank";
    } else if (fuelInclusion.value !== null) {
      initialFuelOffer = "refund";
    }
  }

  const [fuelOffer, setFuelOffer] = useState<FuelOffer>(initialFuelOffer);

  function updateInclusion(newValue: RelocationInclusionInput) {
    if (!fuelInclusion) {
      setFieldValue("inclusions.upsert", [
        ...(values.inclusions?.upsert ?? []),
        newValue,
      ]);
    } else {
      //Ensure that we only have 1 fuel inclusion
      const inclusions =
        values.inclusions?.upsert?.filter((inclusion) => {
          return inclusion.type !== RelocationInclusionType.Fuel;
        }) ?? [];

      const fuelInclusionsToDelete = values.inclusions?.upsert?.filter(
        (inclusion) => {
          if (inclusion.type !== RelocationInclusionType.Fuel) return false;
          return inclusion.id !== newValue.id;
        },
      );

      setFieldValue("inclusions.upsert", [...inclusions, newValue]);
      setFieldValue("inclusions.delete", [
        ...(values.inclusions?.delete ?? []),
        ...(fuelInclusionsToDelete?.map((inclusion) => inclusion.id) ?? []),
      ]);
    }
  }

  async function deleteInclusion() {
    if (fuelInclusion?.id) {
      await setFieldValue("inclusions.delete", [
        ...(values.inclusions?.delete ?? []),
        fuelInclusion.id,
      ]);
    }

    await setFieldValue(
      "inclusions.upsert",
      values.inclusions?.upsert?.filter(
        (inclusion) => inclusion.type !== RelocationInclusionType.Fuel,
      ),
    );
  }

  return (
    <div className="grid grid-cols-3 gap-6 sm:grid-cols-12">
      <RadioButton<FuelOffer>
        className="col-span-full"
        name="fuel_offer"
        label={t("label")}
        value={fuelOffer}
        onChange={(value) => {
          setFuelOffer(value);
          if (value === "no_fuel") {
            return deleteInclusion();
          }

          if (value === "refund") {
            return updateInclusion({
              id: fuelInclusion?.id,
              type: RelocationInclusionType.Fuel,
              description: t("refund"),
              value: null!,
              has_free_tank: false,
            });
          }

          if (value === "free_tank") {
            return updateInclusion({
              id: fuelInclusion?.id,
              type: RelocationInclusionType.Fuel,
              description: null,
              value: null,
              has_free_tank: true,
            });
          }
        }}
        options={[
          {
            label: t("noFuel"),
            value: "no_fuel",
            description: t("noFuelDescription"),
          },
          {
            label: t("freeTank"),
            value: "free_tank",
            description: t("freeTankDescription"),
          },
          {
            label: t("refund"),
            value: "refund",
            description: t("refundDescription"),
          },
        ]}
      />

      {fuelOffer === "refund" ? (
        <>
          <CurrencyInput
            className="col-span-3"
            name={`inclusions.upsert.${fuelInclusionIdx}.value`}
            label={t("fuelAmount")}
            currency={currency ?? values.currency}
          />
          <TextAreaInput
            className="col-span-9"
            name={`inclusions.upsert.${fuelInclusionIdx}.description`}
            label={t("refundConditions")}
            optionalLabel
          />
        </>
      ) : null}
    </div>
  );
}
